import { useEffect } from "react";

/**
 * AssetDetailsModal Component - Modal displaying asset information and WebSocket schema
 * @param {Object} props
 * @param {Object} props.asset - Asset data
 * @param {string} props.displayBaseUrl - Display URL for showing in UI
 * @param {Function} props.onClose - Handler to close the modal
 */
function AssetDetailsModal({ asset, displayBaseUrl, onClose }) {
  useEffect(() => {
    // Disable body scroll when modal is open
    document.body.style.overflow = "hidden";

    // Re-enable body scroll when modal is closed
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2>{asset.name}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          <div className="modal-two-column">
            {/* Left Column - Asset Information and API Endpoints */}
            <div className="modal-column-left">
              {/* Asset Information Section */}
              <div className="modal-section">
                <h3 className="section-title">Asset Information</h3>
                <div className="asset-info-grid">
                  <div className="info-item">
                    <span className="info-label">Asset ID:</span>
                    <code className="info-value">{asset.id}</code>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Type:</span>
                    <span className="info-value">{asset.type}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Industry:</span>
                    <span className="info-value">{asset.industry}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Equipment Count:</span>
                    <span className="info-value">{asset.equipmentCount}</span>
                  </div>
                  <div className="info-item full-width">
                    <span className="info-label">Description:</span>
                    <span className="info-value">{asset.description}</span>
                  </div>
                </div>
              </div>

              {/* API Endpoints Section */}
              <div className="modal-section">
                <h3 className="section-title">WebSocket Endpoint</h3>
                <div className="endpoints-list">
                  <div className="endpoint-item">
                    <div className="endpoint-method ws">WS</div>
                    <div className="endpoint-details">
                      <code className="endpoint-path">
                        {displayBaseUrl
                          .replace("http://", "ws://")
                          .replace("https://", "wss://")}
                        /ws/stream/{asset.id}
                      </code>
                      <p className="endpoint-description">
                        WebSocket stream for real-time data from this asset
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - WebSocket Schema Sample */}
            <div className="modal-column-right">
              <div className="modal-section">
                <h3 className="section-title">WebSocket Message Schema</h3>
                <p className="schema-description">
                  Sample message format received from the WebSocket stream:
                </p>
                <pre className="schema-sample">
                  {`{
  "instance_id.parameter_1": {
    "timestamp": "2025-12-09T10:30:45.123Z",
    "value": 123.45
  },
  "instance_id.parameter_2": {
    "timestamp": "2025-12-09T10:30:45.123Z",
    "value": 78.90
  },
  "instance_id.parameter_3": {
    "timestamp": "2025-12-09T10:30:45.123Z",
    "value": 56.47
  }
}`}
                </pre>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button className="modal-btn secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default AssetDetailsModal;
